import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { chatSessionAPI, chatMessagesAPI, chatSendAPI, chatReadAPI } from '../../api/chat';
import { getVisitorKey } from './visitorKey';
import { subscribeChatOpen } from './chatBus';

// 실시간문의 상태·통신 한 곳 — 문의 목록(방 여러 개)·대화·소켓·보내기·읽음.
//   view 'home' = 인사 + [새 문의하기] + 나의 문의 목록 / 'room' = 한 문의의 대화(activeId, 새 문의는 'new')
//   고객 화면은 안읽음 합계와 '새로운 메시지가 도착했습니다' 툴팁만 보여 준다(사용자 지시).
//   비회원은 이름·연락처(contact)가 저장돼 있거나 이번에 입력(pendingContact)해야 보낼 수 있다.
const SOCKET_BASE = process.env.REACT_APP_API_BASE || '';
const NAMESPACE = '/web-chat';
const TOAST_MS = 6000;
const TYPING_THROTTLE_MS = 1500;
export const NEW_ROOM = 'new';

const token = () => { try { return localStorage.getItem('qwdmtk') || ''; } catch (e) { return ''; } };
const authPayload = () => ({ token: token(), visitor: getVisitorKey() });
const page = () => window.location.pathname;
const byLatest = (a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt);

const appendUnique = (list, message) => (!message || list.some((m) => m.id === message.id) ? list : [...list, message]);
const upsert = (rooms, room) => {
  if (!room) return rooms;
  const next = rooms.some((r) => r.id === room.id) ? rooms.map((r) => (r.id === room.id ? room : r)) : [...rooms, room];
  return next.sort(byLatest);
};

export default function useWebChat({ authKey = '', member = false } = {}) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('home');
  const [activeId, setActiveId] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [contact, setContact] = useState(null);          // 서버에 저장된 이름·연락처(회원 정보 또는 이전 문의)
  const [pendingContact, setPendingContact] = useState(null);
  const [threads, setThreads] = useState({});            // roomId → { messages, hasMore, loaded }
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState('');              // 다른 화면([이 내용으로 상담하기])이 넘긴 첫 글 초안

  const socketRef = useRef(null);
  const stateRef = useRef({ open: false, view: 'home', activeId: null });
  const toastTimer = useRef(null);
  const typingAt = useRef(0);
  stateRef.current = { open, view, activeId };

  const unread = useMemo(() => rooms.reduce((sum, r) => sum + (r.unread || 0), 0), [rooms]);
  const activeRoom = useMemo(() => rooms.find((r) => r.id === activeId) || null, [rooms, activeId]);
  const thread = threads[activeId] || { messages: [], hasMore: false, loaded: activeId === NEW_ROOM };

  const showToast = useCallback(() => {
    setToast(true);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(false), TOAST_MS);
  }, []);

  const patchThread = useCallback((roomId, fn) => {
    setThreads((prev) => ({ ...prev, [roomId]: fn(prev[roomId] || { messages: [], hasMore: false, loaded: false }) }));
  }, []);

  const markRead = useCallback(async (roomId) => {
    if (!roomId || roomId === NEW_ROOM) return;
    setRooms((prev) => prev.map((r) => (r.id === roomId ? { ...r, unread: 0 } : r)));
    try {
      const resp = await chatReadAPI(roomId);
      if (resp && resp.status === 200 && resp.data.room) setRooms((prev) => upsert(prev, resp.data.room));
    } catch (e) { /* 읽음은 실패해도 조용히 */ }
  }, []);

  const loadSession = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await chatSessionAPI();
      if (!resp || resp.status !== 200) return;
      const list = (resp.data.rooms || []).slice().sort(byLatest);
      setRooms(list);
      setContact(resp.data.contact || null);
      if ((resp.data.unread || 0) > 0 && !stateRef.current.open) showToast();
    } catch (e) { /* 네트워크 — 다음 열기 때 다시 */ }
    finally { setLoading(false); }
  }, [showToast]);

  const loadThread = useCallback(async (roomId, before) => {
    if (!roomId || roomId === NEW_ROOM) return;
    setLoading(true);
    try {
      const resp = await chatMessagesAPI(roomId, before);
      if (resp && resp.status === 200) {
        const older = resp.data.messages || [];
        patchThread(roomId, (t) => {
          const ids = new Set(t.messages.map((m) => m.id));
          const merged = before ? [...older.filter((m) => !ids.has(m.id)), ...t.messages] : [...older, ...t.messages.filter((m) => !older.some((o) => o.id === m.id))];
          return { messages: merged, hasMore: Boolean(resp.data.hasMore), loaded: true };
        });
        if (resp.data.room) setRooms((prev) => upsert(prev, resp.data.room));
      }
    } catch (e) { /* 조용히 */ }
    finally { setLoading(false); }
  }, [patchThread]);

  // 소켓 — 회원 룸·방문객 룸에 들어가 직원 답변을 받는다
  useEffect(() => {
    const socket = io(SOCKET_BASE + NAMESPACE, {
      path: '/socket.io',
      auth: authPayload(),
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });
    socketRef.current = socket;
    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));
    socket.on('webchat:message', ({ room, message }) => {
      if (!room || !message) return;
      const fromStaff = message.senderKind !== 'customer';
      const { open: isOpen, view: v, activeId: a } = stateRef.current;
      const viewing = isOpen && v === 'room' && a === room.id;
      setRooms((prev) => upsert(prev, viewing ? { ...room, unread: 0 } : room));
      patchThread(room.id, (t) => (t.loaded ? { ...t, messages: appendUnique(t.messages, message) } : t));
      if (viewing) { if (fromStaff) markRead(room.id); }
      else if (fromStaff) showToast();
    });
    socket.on('webchat:room', ({ room }) => { if (room) setRooms((prev) => upsert(prev, room)); });
    loadSession();
    return () => {
      window.clearTimeout(toastTimer.current);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [loadSession, markRead, patchThread, showToast]);

  // 로그인·로그아웃 — 룸을 갈아 끼우고 목록을 다시 읽는다
  const firstAuth = useRef(true);
  useEffect(() => {
    if (firstAuth.current) { firstAuth.current = false; return; }
    if (socketRef.current) socketRef.current.emit('webchat:auth', authPayload());
    setThreads({});
    setActiveId(null);
    setView('home');
    loadSession();
  }, [authKey, loadSession]);

  // 보고 있던 방이 열린 채 탭에 돌아오면 읽음
  useEffect(() => {
    const onVisible = () => {
      const { open: isOpen, view: v, activeId: a } = stateRef.current;
      if (document.visibilityState === 'visible' && isOpen && v === 'room' && a && a !== NEW_ROOM) markRead(a);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [markRead]);

  // ── 화면 이동 ──
  const openPanel = useCallback(() => { setOpen(true); setToast(false); setError(''); }, []);
  // 다른 화면의 [상담하기] — 패널을 열고 새 문의 화면에 초안을 넣는다(입력창이 가져가면 비운다)
  useEffect(() => subscribeChatOpen((text) => { setOpen(true); setToast(false); setError(''); setView('room'); setActiveId(NEW_ROOM); setDraft(text); }), []);
  const clearDraft = useCallback(() => setDraft(''), []);
  const closePanel = useCallback(() => setOpen(false), []);
  const togglePanel = useCallback(() => { if (stateRef.current.open) closePanel(); else openPanel(); }, [openPanel, closePanel]);
  const goHome = useCallback(() => { setView('home'); setActiveId(null); setError(''); }, []);
  const openRoom = useCallback((roomId) => {
    setView('room');
    setActiveId(roomId);
    setError('');
    const t = threads[roomId];
    if (!t || !t.loaded) loadThread(roomId);
    markRead(roomId);
  }, [threads, loadThread, markRead]);
  const startNew = useCallback(() => { setView('room'); setActiveId(NEW_ROOM); setError(''); }, []);

  // 툴팁을 누르면 안읽은 문의 하나를 바로 연다
  const openUnread = useCallback(() => {
    openPanel();
    const target = rooms.find((r) => r.unread > 0) || rooms[0];
    if (target) openRoom(target.id);
  }, [openPanel, rooms, openRoom]);

  // ── 보내기 ──
  const send = useCallback(async (text) => {
    const t = String(text || '').trim();
    if (!t || sending) return false;
    const roomId = activeId && activeId !== NEW_ROOM ? activeId : undefined;
    setSending(true);
    setError('');
    try {
      const resp = await chatSendAPI({ roomId, text: t, page: page(), contact: pendingContact || undefined });
      if (!resp || resp.status !== 201) {
        setError((resp && resp.data && resp.data.message) || '메시지를 보내지 못했습니다.');
        return false;
      }
      const { room, message, contactMessage } = resp.data;
      setRooms((prev) => upsert(prev, room));
      patchThread(room.id, (prev) => {
        const base = prev.loaded || roomId ? prev : { messages: [], hasMore: false, loaded: true };
        const withInfo = contactMessage ? appendUnique(base.messages, contactMessage) : base.messages;
        return { ...base, loaded: true, messages: appendUnique(withInfo, message) };
      });
      if (!roomId) setActiveId(room.id);
      if (room.contact && room.contact.name && room.contact.tel) setContact(room.contact);
      setPendingContact(null);
      return true;
    } catch (e) {
      setError('메시지를 보내지 못했습니다.');
      return false;
    } finally {
      setSending(false);
    }
  }, [sending, activeId, pendingContact, patchThread]);

  const loadMore = useCallback(() => {
    if (!activeId || activeId === NEW_ROOM || !thread.hasMore || loading || !thread.messages.length) return;
    loadThread(activeId, thread.messages[0].at);
  }, [activeId, thread, loading, loadThread]);

  const startAsGuest = useCallback((c) => { setPendingContact(c && c.name && c.tel ? c : null); setError(''); }, []);

  // 비회원은 저장된 이름·연락처가 없고 이번에 입력하지도 않았으면 시작 폼부터
  const needsContact = !member && !pendingContact && !(contact && contact.name && contact.tel);

  // 입력 중 — 1.5초에 한 번만, 비우면 stop (새 문의는 방이 없어 보내지 않는다)
  const typing = useCallback((stop = false) => {
    const socket = socketRef.current;
    if (!socket || !activeId || activeId === NEW_ROOM) return;
    const now = Date.now();
    if (!stop && now - typingAt.current < TYPING_THROTTLE_MS) return;
    typingAt.current = stop ? 0 : now;
    socket.emit('webchat:typing', { roomId: activeId, stop });
  }, [activeId]);

  return {
    open, view, activeId, activeRoom, rooms, contact, pendingContact, needsContact,
    messages: thread.messages, hasMore: thread.hasMore, loading, sending, unread, toast, connected, error, draft, clearDraft,
    openPanel, closePanel, togglePanel, goHome, openRoom, startNew, openUnread, send, loadMore, startAsGuest, typing,
    dismissToast: () => setToast(false),
  };
}
